import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum } from "./theme";
import { SkeletonCard, SourceChip, SectionTitle, DeltaBadge } from "./components";
import { pulseGet } from "./api";
import { BUCKET_COLORS } from "./CityBubbleChart";

/** Italy Deep Dive mini-embed: Italian cities in the EU «busiest cities» ranking, latest year + YoY. */
export default function ItalyCitiesPanel({ dm }) {
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    pulseGet("cities", { top: 50, country: "IT" })
      .then((d) => on && setData(d)).catch(() => on && setData(null)).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  const rows = useMemo(() => (data?.country_rows || []).slice(0, 10), [data]);
  const max = rows.length ? rows[0].nights_total : 1;

  if (loading) return <SkeletonCard h={300} dm={dm} />;
  if (!rows.length) return null;
  return (
    <section className="mb-10" data-testid="italy-cities-panel" aria-labelledby="italy-cities-title">
      <SectionTitle dm={dm} right={
        <Link to={withLang("/pulse/cities")} className="text-xs font-semibold" style={{ color: T.accent }} data-testid="italy-cities-more">
          {t("italy_cities_more")}
        </Link>}>
        <span id="italy-cities-title">{t("italy_cities_title")}</span>
      </SectionTitle>
      <p className="text-xs mb-4 max-w-3xl" style={{ color: T.muted }}>
        {t("italy_cities_desc", { year: data.year, prev: data.prev_year || "—", universe: data.universe })}
      </p>
      <div className="p-4 md:p-5" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <ol className="flex flex-col gap-3" data-testid="italy-cities-list">
          {rows.map((r) => (
            <li key={r.city} className="grid grid-cols-[2.25rem_1fr] gap-x-3 items-center text-xs" data-testid={`italy-city-${r.city}`}>
              <span className="inline-flex items-center justify-center h-7 w-9 text-[11px] font-bold tabular-nums"
                style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }} title={t("cities_tt_rank")}>#{r.rank}</span>
              <div className="min-w-0">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="font-semibold truncate" style={{ color: T.text }}>
                    {r.name}{r.greater_city && <span className="ml-1 text-[9px] uppercase" style={{ color: T.muted }}>{t("cities_greater")}</span>}
                  </span>
                  <span className="flex items-baseline gap-3 tabular-nums whitespace-nowrap" style={{ color: T.muted }}>
                    <span className="font-semibold" style={{ color: T.text }}>{fmtCompact(r.nights_total, lang)}</span>
                    <DeltaBadge pct={r.yoy_nights_pct} dm={dm} suffix={t("cities_tt_yoy", { prev: r.prev_year })} />
                  </span>
                </div>
                <div className="h-2 mt-1 w-full" style={{ background: T.surface2 }}>
                  <div className="h-2" style={{ width: `${Math.max(1.5, (r.nights_total / max) * 100)}%`, background: BUCKET_COLORS[r.bucket], transition: "width .6s" }} />
                </div>
                <div className="text-[10px] tabular-nums mt-0.5" style={{ color: T.muted }}>
                  {fmtNum(r.foreign_share_pct, lang, 0)}% {t("italy_cities_foreign")} · {fmtNum(r.nights_per_resident, lang, 1)} {t("italy_cities_npr")}
                </div>
              </div>
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-1.5 mt-4">
          {(data.sources || []).map((s) => <SourceChip key={s.dataset_code} source={s.source_org} dataset={s.dataset_code} period={s.period} dm={dm} testid={`italy-cities-source-${s.dataset_code}`} />)}
        </div>
      </div>
    </section>
  );
}

